import { useState } from 'react'
import { isHTTPError } from 'ky'
import { differenceInCalendarDays, format } from 'date-fns'
import { UTCDate } from '@date-fns/utc'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FileText, ExternalLink, CalendarClock, Archive } from 'lucide-react'
import type { BridgeLetter } from '@common/types'
import { bridgeLetterCapDate, isBridgeLetterCurrentlyValid } from '@common/utils/validity'
import { archiveBridgeLetterApi, extendBridgeLetterApi, getBridgeLetterHistoryApi } from '@frontend/lib/api'
import { usePermissions } from '@frontend/hooks/usePermissions'
import { Card, CardContent, CardHeader, CardTitle } from '@frontend/components/ui/card'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import { StatusBadge } from '@frontend/components/ui/status-badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/components/ui/dialog'
import { EditHistoryTimeline } from '@frontend/components/history'

interface BridgeLetterCardProps {
  bridgeLetter: BridgeLetter
  /** The review's `auditPeriodEnd` — derives the 6-month cap. */
  auditPeriodEnd: string | null
  /** Owning vendor id, for query invalidation. */
  vendorId: string
  /** Pass `false` when a newer approved review exists — suppresses the "Bridge Letter until …" claim. */
  isLatestReview?: boolean
}

const formatDay = (date: string) => format(new UTCDate(date), 'MMM d, yyyy')

export function BridgeLetterCard({
  bridgeLetter,
  auditPeriodEnd,
  vendorId,
  isLatestReview = true,
}: BridgeLetterCardProps) {
  const { can } = usePermissions()
  const queryClient = useQueryClient()

  const isValid = isBridgeLetterCurrentlyValid(bridgeLetter)
  const cap = auditPeriodEnd ? bridgeLetterCapDate(auditPeriodEnd) : null
  // endDate can never exceed cap by construction, so at-cap == endDate reached cap.
  const atCap = cap != null && differenceInCalendarDays(cap, new UTCDate(bridgeLetter.endDate)) <= 0

  const extendMutation = useMutation({
    mutationFn: () => extendBridgeLetterApi(bridgeLetter.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review', String(bridgeLetter.reviewId)] })
      queryClient.invalidateQueries({ queryKey: ['vendor-reviews', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      queryClient.invalidateQueries({ queryKey: ['bridge-letter', bridgeLetter.id, 'history'] })
      toast.success('Bridge letter extended by 30 days')
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const data = err.data as { error?: string }
        toast.error(data.error || 'Failed to extend bridge letter')
      } else {
        toast.error('Failed to extend bridge letter')
      }
    },
  })

  const canExtend = can('bridgeLetters.extend')
  const canArchive = can('reviews.create')
  const [showArchiveDialog, setShowArchiveDialog] = useState(false)

  // Fetch the letter's edit history separately (like review history), rather than
  // bundling it into the review-detail payload.
  const { data: historyData } = useQuery({
    queryKey: ['bridge-letter', bridgeLetter.id, 'history'],
    queryFn: () => getBridgeLetterHistoryApi(bridgeLetter.id),
  })

  const archiveMutation = useMutation({
    mutationFn: () => archiveBridgeLetterApi(bridgeLetter.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['review', String(bridgeLetter.reviewId)] })
      queryClient.invalidateQueries({ queryKey: ['vendor-reviews', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['vendor', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      setShowArchiveDialog(false)
      toast.success('Bridge letter archived — you can upload a replacement')
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const data = err.data as { error?: string }
        toast.error(data.error || 'Failed to archive bridge letter')
      } else {
        toast.error('Failed to archive bridge letter')
      }
    },
  })

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Bridge Letter
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1.5">
            {!isValid ? (
              <StatusBadge label={atCap ? 'Expired — at 6-month maximum' : 'Expired — extend'} variant="error" />
            ) : isLatestReview ? (
              <StatusBadge label={`Bridge Letter until ${formatDay(bridgeLetter.endDate)}`} variant="info" />
            ) : (
              // Valid letter, but a newer approved review exists — don't claim the bridge label.
              <StatusBadge label="Superseded by a newer review" variant="neutral" />
            )}
            <div className="text-sm text-muted-foreground">
              Covers {formatDay(bridgeLetter.startDate)} – {formatDay(bridgeLetter.endDate)}
            </div>
            <div className="text-xs text-muted-foreground">{bridgeLetter.fileName}</div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`/api/bridge-letters/${bridgeLetter.id}/file`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              View PDF <ExternalLink className="h-3 w-3" />
            </a>
            {canExtend && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => extendMutation.mutate()}
                disabled={extendMutation.isPending || atCap}
              >
                {extendMutation.isPending ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <CalendarClock className="h-4 w-4 mr-1" />
                )}
                Extend 30 days
              </Button>
            )}
            {canArchive && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowArchiveDialog(true)}
                disabled={archiveMutation.isPending}
              >
                <Archive className="h-4 w-4 mr-1" />
                Archive
              </Button>
            )}
          </div>
        </div>

        {canExtend && atCap && (
          <p className="text-xs text-muted-foreground">
            This bridge letter has reached the 6-month maximum and cannot be extended further.
          </p>
        )}

        <div>
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">History</div>
          <EditHistoryTimeline edits={historyData?.edits ?? []} emptyMessage="No extensions recorded yet." />
        </div>
      </CardContent>

      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive bridge letter</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Archiving removes this bridge letter from the review. You can upload a replacement afterward. This
                  action cannot be undone.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => archiveMutation.mutate()} disabled={archiveMutation.isPending}>
              {archiveMutation.isPending ? <Spinner size="sm" className="mr-2" /> : null}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
